import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MaterialProps } from '@partsbadger/types';
import { Button, Input, Spin } from 'antd';
import { LoadingOutlined, FilterOutlined, UpOutlined } from '@ant-design/icons';
import { StepCard } from '../StepCard';
import PlasticMaterialPreferencesModal from './PlasticMaterialPreferencesModal';
import { FinishStore } from '@/stores';
import { FilterPill } from '../FilterPill';
import { isPlasticNeedingColor } from './helpers';
interface IProduct {
    material?: number;
    finishes?: number[];
    custom_material?: string;
}
interface IMaterialStepProps {
    stepIndex?: number;
    materials: MaterialProps[];
    topMaterials: MaterialProps[];
    materialSelected: MaterialProps | null;
    manufacturing_type: string;
    isHobbyist: boolean;
    loading: boolean;
    customMaterial: string | null;
    onHandleUpdate: (value: { product: IProduct }) => void;
    onPendingCustomChange?: (stepIndex: number, isPending: boolean) => void;
}

const { Search } = Input;

export function MaterialStep({
    stepIndex = 1,
    materials,
    topMaterials,
    materialSelected,
    manufacturing_type,
    isHobbyist,
    loading,
    customMaterial,
    onHandleUpdate,
    onPendingCustomChange,
}: IMaterialStepProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All Materials');
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [materialColorMode, setMaterialColorMode] = useState<string | null>(null);
    const [selectedMaterial, setSelectedMaterial] = useState<MaterialProps | null>(null);
    const [includeSelectedMaterialInList, setIncludeSelectedMaterialInList] = useState(true);
    const [pendingCustomMaterialId, setPendingCustomMaterialId] = useState<number | null>(null);

    useEffect(() => {
        setSelectedCategory('All Materials');
        setSearchQuery('');
    }, [manufacturing_type]);

    let data = materials;

    if (manufacturing_type) {
        data = data.filter(item => {
            return (
                Array.isArray(item.manufacturing_types) &&
                item.manufacturing_types.some(type => type.toLowerCase() === manufacturing_type.toLowerCase())
            );
        });
    }

    if (isHobbyist) {
        data = data?.filter(item => {
            return item.name !== 'Review 2D' && item.name !== 'Custom';
        });
    }

    const custom = data.find(item => item.name === 'Custom');

    if (custom) {
        // Exclude custom from list
        data = data.filter(item => item.id !== custom?.id);
    }

    const eligibleMaterialIds = useMemo(() => new Set(data.map(material => material.id)), [data]);

    const displayMaterialSelected = pendingCustomMaterialId != null && custom ? custom : materialSelected;

    const shouldHideMaterial = useCallback(
        (material?: MaterialProps) => isHobbyist && isPlasticNeedingColor(material),
        [isHobbyist]
    );

    const visibleMaterials = useMemo(() => data.filter(material => !shouldHideMaterial(material)), [
        data,
        shouldHideMaterial,
    ]);

    useEffect(() => {
        onPendingCustomChange?.(stepIndex, pendingCustomMaterialId != null);
    }, [stepIndex, pendingCustomMaterialId, onPendingCustomChange]);

    const materialTypeCounts = visibleMaterials.reduce<Record<string, number>>((acc, item) => {
        const type = item?.material_type || 'Unspecified';

        acc[type] = (acc[type] || 0) + 1;

        return acc;
    }, {});

    const materialTypeList: Array<{ name: string; count: number }> = [
        { name: 'All Materials', count: visibleMaterials.length },
        ...Object.entries(materialTypeCounts).map(([name, count]) => ({
            name: name || 'Unspecified',
            count,
        })),
    ];

    const handleUpdate = (product: IProduct) => {
        const payload = {
            product: product,
        };

        onHandleUpdate(payload);
    };

    const handleShowPlasticMaterialPreferences = (material: MaterialProps, field: string) => {
        if (material) {
            setSelectedMaterial(material);
            setMaterialColorMode(field);
        }
    };

    const handleMaterialSelect = (id: number) => {
        if (custom && id === custom.id) {
            onPendingCustomChange?.(stepIndex, true);
            setPendingCustomMaterialId(custom.id);
            handleUpdate({ material: custom.id, custom_material: '' });
            return;
        }
        onPendingCustomChange?.(stepIndex, false);
        setPendingCustomMaterialId(null);
        const material = data?.find(item => item.id === id);
        const containsClearInLabel = /clear/i.test(material?.name || '');

        if (isPlasticNeedingColor(material)) {
            if (isHobbyist) {
                return;
            }

            if (material) {
                handleShowPlasticMaterialPreferences(material, 'colored');
            }

            return true;
        }

        if (material?.material_type === 'Plastic' && containsClearInLabel) {
            handleShowPlasticMaterialPreferences(material, 'transparent');

            return true;
        }
        const payload: IProduct = { material: id };
        payload.custom_material = '';
        handleUpdate(payload);
    };

    const handleCategorySelect = (categoryId: string) => {
        setSelectedCategory(categoryId);
        setSearchQuery(''); // Clear search when changing category
    };

    const handleCustomMaterialUpdate = (value: string) => {
        if (!custom?.id) {
            return;
        }

        onPendingCustomChange?.(stepIndex, false);
        handleUpdate({ material: custom.id, custom_material: value });
        setPendingCustomMaterialId(null);
    };

    // Filter materials based on search and category
    const filteredMaterials = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        let materials = [...visibleMaterials];

        if (selectedCategory !== 'All Materials') {
            const getMaterialType = selectedCategory === 'Unspecified' ? '' : selectedCategory;
            materials = materials.filter(material => material?.material_type === getMaterialType);
        }

        if (custom) {
            materials = [custom, ...materials];
        }

        if (query) {
            materials = materials.filter(material => material?.name?.toLowerCase().includes(query));

            if (materials.length === 0) {
                return materials;
            }
        }

        const selectedMaterialIsValidForCurrentProcess = materialSelected
            ? visibleMaterials.some(m => m.id === materialSelected.id)
            : false;

        const shouldShowSelected =
            selectedMaterialIsValidForCurrentProcess &&
            (selectedCategory === 'All Materials' || materialSelected?.material_type === selectedCategory);

        if (displayMaterialSelected && shouldShowSelected && includeSelectedMaterialInList) {
            materials = materials.filter(m => m?.id !== displayMaterialSelected.id);
            materials = [displayMaterialSelected, ...materials];
        }

        return materials;
    }, [visibleMaterials, searchQuery, selectedCategory, displayMaterialSelected, custom]);

    // Filter popular (top) materials
    const filteredTopMaterials = useMemo(() => {
        let filtered = topMaterials.filter(material => eligibleMaterialIds.has(material.id));

        if (selectedCategory !== 'All Materials') {
            filtered = filtered.filter(material => material?.material_type === selectedCategory);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(material => material?.name?.toLowerCase().includes(query));
        }

        return filtered.filter(material => !shouldHideMaterial(material));
    }, [topMaterials, searchQuery, selectedCategory, shouldHideMaterial, eligibleMaterialIds]);

    const popularMaterials = filteredTopMaterials;

    // Remove popular materials from other materials
    const otherMaterials = useMemo(() => {
        if (!popularMaterials.length) return filteredMaterials;

        const popularIds = new Set(popularMaterials.map(m => m.id));

        return filteredMaterials.filter(material => !popularIds.has(material.id));
    }, [filteredMaterials, popularMaterials]);

    const handlePlasticMaterialPreferences = (field: string, value: string) => {
        if (field === 'transparent' && value === 'yes') {
            const flamePolishFinish = FinishStore.finishes?.find(finish => finish.name === 'Flame Polish');

            if (flamePolishFinish?.id) {
                handleUpdate({
                    material: selectedMaterial?.id,
                    finishes: [flamePolishFinish.id],
                    custom_material: '',
                });
            }
            setMaterialColorMode(null);

            return;
        }

        if (field === 'colored') {
            if (custom?.id && selectedMaterial?.name && value) {
                handleUpdate({
                    material: custom.id,
                    custom_material: `${selectedMaterial.name} ${value}`,
                });
            }

            setMaterialColorMode(null);

            return;
        }

        if (selectedMaterial?.id) {
            handleUpdate({ material: selectedMaterial.id, custom_material: '' });
        }

        setMaterialColorMode(null);
    };

    const hasCategoryFilter = selectedCategory !== 'All Materials';

    return (
        <div data-testid="material-step-shell" className="space-y-4">
            <div>
                <h3 data-testid="material-step-heading" className="text-lg font-semibold text-slate-900">
                    Material Selection
                </h3>
                <p className="text-slate-600 text-sm">Choose the material that matches your performance requirements</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px]">
                    <Search
                        data-testid="material-search-input"
                        placeholder="Search materials..."
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value);
                            setIncludeSelectedMaterialInList(false);
                        }}
                        allowClear
                    />
                </div>
                <Button
                    data-testid="material-filter-btn"
                    type={hasCategoryFilter ? 'primary' : 'default'}
                    size="middle"
                    icon={filtersExpanded ? <UpOutlined /> : <FilterOutlined />}
                    onClick={() => setFiltersExpanded(prev => !prev)}
                    className="shrink-0"
                    style={
                        !hasCategoryFilter && !filtersExpanded
                            ? {
                                  borderStyle: 'dashed',
                                  borderWidth: 1.5,
                                  color: '#3b82f6',
                                  borderColor: '#93c5fd',
                                  fontWeight: 500,
                              }
                            : undefined
                    }
                >
                    {filtersExpanded ? 'Hide filters' : 'Filter by type'}
                    {hasCategoryFilter && !filtersExpanded && (
                        <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                            1
                        </span>
                    )}
                </Button>
            </div>
            {!filtersExpanded && (
                <p className="text-xs text-slate-500">
                    Tip: use &quot;Filter by type&quot; to narrow by Plastic, Metal, and other material types.
                </p>
            )}

            <AnimatePresence initial={false}>
                {filtersExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="space-y-2 pt-1 pb-2 border-b border-slate-100">
                            <h4 className="text-xs font-medium text-slate-700">Filter by Material Type</h4>
                            <div className="flex flex-wrap gap-1">
                                {materialTypeList?.map(category => {
                                    const isSelected = selectedCategory === category.name;
                                    const categoryCount = category.count;

                                    return (
                                        <FilterPill
                                            key={category.name}
                                            label={category.name}
                                            count={categoryCount}
                                            isSelected={isSelected}
                                            onSelect={handleCategorySelect}
                                            testId="material-filter-pill"
                                            dataMaterialType={category.name}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Results */}
            {filteredMaterials.length === 0 ? (
                <div data-testid="material-empty-state" className="text-center py-8">
                    <h4 className="text-sm font-medium text-slate-600 mb-1">No materials found</h4>
                    {custom && (
                        <span className="flex flex-row items-center cursor-pointer justify-center mb-2">
                            Can't find your material? Please select{' '}
                            <Button
                                type="link"
                                onClick={() => {
                                    handleCategorySelect('All Materials');
                                    setSearchQuery('Custom');
                                    if (custom?.id != null) {
                                        onPendingCustomChange?.(stepIndex, true);
                                        setPendingCustomMaterialId(custom.id);
                                        handleUpdate({ material: custom.id, custom_material: '' });
                                    }
                                }}
                                className="p-1"
                            >
                                Custom
                            </Button>
                        </span>
                    )}

                    <Button
                        type="default"
                        size="small"
                        onClick={() => {
                            setSearchQuery('');
                            setSelectedCategory('All Materials');
                        }}
                    >
                        Clear all filters
                    </Button>
                </div>
            ) : (
                <div className="space-y-3 overflow-y-auto pr-2" style={{ maxHeight: 'min(45vh, 380px)' }}>
                    {popularMaterials?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                {selectedCategory === 'all'
                                    ? 'Popular Choices'
                                    : `Popular ${materialTypeList?.find(c => c.name === selectedCategory)?.name || ''}`}
                            </h4>
                            <motion.div layout className="grid gap-1">
                                {popularMaterials?.map(item => (
                                    <StepCard
                                        key={item?.id}
                                        entity="material"
                                        stepInfo={{
                                            id: item.id,
                                            name: item.name,
                                            materialType: item.material_type,
                                            colored: item.colored,
                                        }}
                                        selectedId={displayMaterialSelected?.id}
                                        onSelect={handleMaterialSelect}
                                        customValue={customMaterial}
                                        onUpdateCustom={handleCustomMaterialUpdate}
                                        isPopular={true}
                                    />
                                ))}
                            </motion.div>
                        </div>
                    )}

                    {otherMaterials?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">
                                {selectedCategory === 'All Materials'
                                    ? 'Other Materials'
                                    : `${materialTypeList.find(c => c.name === selectedCategory)?.name || ''}`}
                            </h4>
                            <Spin spinning={loading} indicator={<LoadingOutlined />}>
                                <div className="grid gap-1 pl-1">
                                    {otherMaterials?.map(item => (
                                        <StepCard
                                            key={item?.id}
                                            entity="material"
                                            stepInfo={{
                                                id: item.id,
                                                name: item.name,
                                                materialType: item.material_type,
                                                colored: item.colored,
                                            }}
                                            selectedId={displayMaterialSelected?.id}
                                            onSelect={handleMaterialSelect}
                                            customValue={customMaterial}
                                            onUpdateCustom={handleCustomMaterialUpdate}
                                        />
                                    ))}
                                </div>
                            </Spin>
                        </div>
                    )}
                </div>
            )}
            {materialColorMode && (
                <PlasticMaterialPreferencesModal
                    showMaterialColorModal={materialColorMode}
                    onHandleSubmit={handlePlasticMaterialPreferences}
                    handleModalCancel={() => {
                        setMaterialColorMode(null);
                    }}
                />
            )}
        </div>
    );
}
